// ============================================================
// HOOKED — Angler3D.js        THE BODY, THE BOAT, THE ROD
//
// Built from primitives. Zero .glb, zero Blender, zero assets,
// zero load time — per ie-being-models. An ink-line contour
// (BackSide shell, flat colour) on every major mass is what
// makes a primitive figure read as DRAWN rather than as cheap.
//
// LAWS IN FORCE
//   • THE BODY OWNS NOTHING. No state, no position props, no
//     re-renders. It is handed refs and reads them inside
//     useFrame. A body moving at 60fps must not touch React.
//   • THE AURA RENDERS OUTSIDE THE BODY (PRIME §1.18) so it
//     survives real art later. Same for the lantern rim.
//   • NO ◆ SIGIL ON THE BODY (PRIME §1.15). The sigil lives in
//     the world and the chrome. Not one facet of it is here.
//   • The walk cycle advances with SPEED, not with the clock,
//     or he moonwalks in place when he stops.
//   • ONE carried light, castShadow={false}. A second shadow
//     caster is the most expensive thing in a night scene and
//     buys almost nothing.
//
// THE SCALE REFERENCE the lake never had — everything else in
// the renderer measures itself against these three numbers.
// ============================================================

import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

export const BODY_H   = 1.86;   // crown of the cap
export const BODY_EYE = 1.62;   // eye height
export const BODY_R   = 0.42;   // footprint radius — collision uses this

const INK = "#080a0e";          // the contour. Nothing in the Empire is honestly black.

// A contour shell. One extra draw call per mass, and it is the
// whole reason this doesn't look like a mannequin.
function Ink({ scale = 1.055, position = [0, 0, 0], children }) {
  return (
    <mesh scale={scale} position={position} raycast={() => null}>
      {children}
      <meshBasicMaterial color={INK} side={THREE.BackSide} fog={false} />
    </mesh>
  );
}

// ── THE ANGLER ───────────────────────────────────────────────
// posRef   : THREE.Vector3, world position of the feet
// yawRef   : { current: radians }
// speedRef : { current: 0 still · 1 walk · 1.8 run }
// mode     : "walk" | "boat" | "cast"   (changes when the GAME changes)
export function Angler3D({ posRef, yawRef, speedRef, mode = "walk", outfit = {}, castRef }) {
  const root  = useRef();
  const hips  = useRef();
  const chest = useRef();
  const legL  = useRef();
  const legR  = useRef();
  const armL  = useRef();
  const armR  = useRef();
  const rod   = useRef();
  const phase = useRef(0);
  const bob   = useRef(0);

  const coatHi = outfit.coat?.[0] || "#f5cd5e";
  const coatLo = outfit.coat?.[1] || "#a97e28";
  const cap    = outfit.cap      || "#a3773c";
  const capBand= outfit.capBand  || "#5c421f";
  const aura   = outfit.aura     || null;
  const back   = outfit.back     || null;
  const backCol= outfit.backColor|| "#a3773c";

  // Materials are memoised by colour so equipping a coat costs one
  // material, not one per frame.
  const mats = useMemo(() => ({
    coat: new THREE.MeshStandardMaterial({ color: coatHi, roughness: 0.72, metalness: 0.06 }),
    coatLo: new THREE.MeshStandardMaterial({ color: coatLo, roughness: 0.8, metalness: 0.04 }),
    cap: new THREE.MeshStandardMaterial({ color: cap, roughness: 0.78 }),
    band: new THREE.MeshStandardMaterial({ color: capBand, roughness: 0.85 }),
    skin: new THREE.MeshStandardMaterial({ color: "#d8b28c", roughness: 0.66 }),
    boot: new THREE.MeshStandardMaterial({ color: "#241a10", roughness: 0.9 }),
    wood: new THREE.MeshStandardMaterial({ color: "#6b4a22", roughness: 0.7 }),
    brass: new THREE.MeshStandardMaterial({ color: "#d9a83f", roughness: 0.28, metalness: 0.85,
      emissive: "#a97e28", emissiveIntensity: 0.22 }),
    pack: new THREE.MeshStandardMaterial({ color: backCol, roughness: 0.82 }),
  }), [coatHi, coatLo, cap, capBand, backCol]);

  useFrame((state, dt) => {
    const g = root.current;
    if (!g || !posRef.current) return;
    const p = posRef.current;
    const d = Math.min(dt, 0.05);          // frame-rate independent, and hitch-proof

    g.position.set(p.x, p.y, p.z);

    // shortest-arc eased turn — snapping between headings is what
    // made it feel 2D.
    let diff = (yawRef.current || 0) - g.rotation.y;
    while (diff >  Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    g.rotation.y += diff * Math.min(1, d * 12);

    const sp = speedRef.current || 0;
    phase.current += sp * d * 8.5;         // ← SPEED, not clock
    const t = state.clock.elapsedTime;

    const swing = Math.sin(phase.current);
    const lean  = Math.min(0.17, sp * 0.075);

    if (legL.current) legL.current.rotation.x =  swing * 0.72 * Math.min(1, sp);
    if (legR.current) legR.current.rotation.x = -swing * 0.72 * Math.min(1, sp);

    if (mode === "cast") {
      // Both hands to the rod, braced. The rod is the pose.
      const k = castRef?.current ?? 0;      // 0..1, the whip
      if (armR.current) armR.current.rotation.x = -1.15 - k * 0.9;
      if (armL.current) armL.current.rotation.x = -0.85 - k * 0.6;
      if (rod.current)  rod.current.rotation.x  = -0.55 + k * 1.35;
    } else {
      if (armR.current) armR.current.rotation.x = -swing * 0.6 * Math.min(1, sp) - 0.12;
      if (armL.current) armL.current.rotation.x =  swing * 0.6 * Math.min(1, sp) - 0.12;
      if (rod.current)  rod.current.rotation.x  = -0.28 + Math.sin(t * 0.8) * 0.02;
    }

    // Bob with the stride; BREATHE when still. A perfectly still
    // figure is a statue; a breathing one is waiting.
    const stride = Math.abs(Math.sin(phase.current * 1.0)) * 0.055 * Math.min(1, sp);
    const breath = (1 - Math.min(1, sp)) * Math.sin(t * 0.95) * 0.018;
    bob.current += ((stride + breath) - bob.current) * Math.min(1, d * 12);
    if (hips.current) {
      hips.current.position.y = 0.9 + bob.current;
      hips.current.rotation.z = swing * 0.05 * Math.min(1, sp);
    }
    if (chest.current) {
      chest.current.rotation.x = lean;
      const b = 1 + (1 - Math.min(1, sp)) * Math.sin(t * 0.95) * 0.022;
      chest.current.scale.set(b, 1, b);
    }
  });

  return (
    <group ref={root}>
      {/* CONTACT SHADOW — his weight on the boards. A painted blob
          rather than a shadow map: free, and it never flickers. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.012, 0]} raycast={() => null}>
        <circleGeometry args={[0.46, 20]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.45} depthWrite={false} />
      </mesh>

      <group ref={hips} position={[0, 0.9, 0]}>
        {/* legs — pivoting from the hip */}
        {[["l", -0.13], ["r", 0.13]].map(([k, x]) => (
          <group key={k} ref={k === "l" ? legL : legR} position={[x, 0, 0]}>
            <mesh position={[0, -0.28, 0]} castShadow>
              <capsuleGeometry args={[0.085, 0.4, 4, 8]} />
              <primitive object={mats.coatLo} attach="material" />
            </mesh>
            <Ink scale={1.09} position={[0, -0.28, 0]}><capsuleGeometry args={[0.085, 0.4, 4, 8]} /></Ink>
            <mesh position={[0, -0.58, 0.03]} castShadow>
              <boxGeometry args={[0.17, 0.13, 0.3]} />
              <primitive object={mats.boot} attach="material" />
            </mesh>
          </group>
        ))}

        <group ref={chest}>
          {/* the coat — tapered, the biggest mass, so it carries
              the silhouette and the ink line */}
          <mesh position={[0, 0.3, 0]} castShadow>
            <cylinderGeometry args={[0.235, 0.325, 0.78, 12]} />
            <primitive object={mats.coat} attach="material" />
          </mesh>
          <Ink scale={1.05} position={[0, 0.3, 0]}><cylinderGeometry args={[0.235, 0.325, 0.78, 12]} /></Ink>
          {/* the coat's shadowed hem */}
          <mesh position={[0, -0.07, 0]}>
            <cylinderGeometry args={[0.327, 0.33, 0.12, 12]} />
            <primitive object={mats.coatLo} attach="material" />
          </mesh>
          {/* belt */}
          <mesh position={[0, 0.02, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.3, 0.028, 6, 16]} />
            <primitive object={mats.band} attach="material" />
          </mesh>
          <mesh position={[0, 0.02, 0.3]}>
            <boxGeometry args={[0.09, 0.07, 0.035]} />
            <primitive object={mats.brass} attach="material" />
          </mesh>

          {/* arms */}
          {[["l", -0.3, armL], ["r", 0.3, armR]].map(([k, x, r]) => (
            <group key={k} ref={r} position={[x, 0.58, 0]}>
              <mesh position={[0, -0.24, 0]} castShadow>
                <capsuleGeometry args={[0.068, 0.34, 4, 8]} />
                <primitive object={mats.coat} attach="material" />
              </mesh>
              <Ink scale={1.1} position={[0, -0.24, 0]}><capsuleGeometry args={[0.068, 0.34, 4, 8]} /></Ink>
              <mesh position={[0, -0.46, 0]}>
                <sphereGeometry args={[0.072, 10, 8]} />
                <primitive object={mats.skin} attach="material" />
              </mesh>
            </group>
          ))}

          {/* the rod — held in the right hand, and the reason he
              is here at all */}
          <group ref={rod} position={[0.3, 0.14, 0.08]} rotation={[-0.28, 0, -0.13]}>
            <mesh position={[0, 0.62, 0]} castShadow>
              <cylinderGeometry args={[0.008, 0.021, 1.35, 6]} />
              <primitive object={mats.wood} attach="material" />
            </mesh>
            <mesh position={[0, 0.06, 0]}>
              <cylinderGeometry args={[0.026, 0.026, 0.2, 8]} />
              <primitive object={mats.band} attach="material" />
            </mesh>
            {/* the reel */}
            <mesh position={[0.045, 0.2, 0]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.05, 0.05, 0.035, 12]} />
              <primitive object={mats.brass} attach="material" />
            </mesh>
          </group>

          {/* the creel / pack — only when a BACK item is worn */}
          {back && (
            <group position={[0, 0.3, -0.3]}>
              <mesh castShadow>
                <boxGeometry args={[0.34, 0.3, 0.17]} />
                <primitive object={mats.pack} attach="material" />
              </mesh>
              <Ink scale={1.07}><boxGeometry args={[0.34, 0.3, 0.17]} /></Ink>
            </group>
          )}

          {/* neck + head */}
          <mesh position={[0, 0.74, 0]}>
            <cylinderGeometry args={[0.062, 0.075, 0.1, 8]} />
            <primitive object={mats.skin} attach="material" />
          </mesh>
          <group position={[0, 0.9, 0]}>
            <mesh castShadow>
              <sphereGeometry args={[0.155, 16, 14]} />
              <primitive object={mats.skin} attach="material" />
            </mesh>
            <Ink scale={1.05}><sphereGeometry args={[0.155, 16, 14]} /></Ink>
            {/* the cap — crown, brim, band */}
            <mesh position={[0, 0.09, 0]} castShadow>
              <sphereGeometry args={[0.163, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2]} />
              <primitive object={mats.cap} attach="material" />
            </mesh>
            <mesh position={[0, 0.075, 0]}>
              <cylinderGeometry args={[0.168, 0.168, 0.042, 16]} />
              <primitive object={mats.band} attach="material" />
            </mesh>
            <mesh position={[0, 0.062, 0.11]} rotation={[-0.16, 0, 0]}>
              <cylinderGeometry args={[0.19, 0.19, 0.02, 16, 1, false, -0.9, 1.8]} />
              <primitive object={mats.cap} attach="material" />
            </mesh>
          </group>
        </group>
      </group>

      {/* ── THE LANTERN. The light WALKS. ────────────────────────
          Raising ambient makes a night scene evenly visible, which
          kills the night AND kills the 3D. A light that moves means
          every surface changes as he crosses the deck. */}
      <group position={[-0.34, 1.0, 0.16]}>
        <mesh>
          <sphereGeometry args={[0.055, 10, 8]} />
          <meshBasicMaterial color="#ffe9a8" toneMapped={false} />
        </mesh>
        <mesh>
          <sphereGeometry args={[0.1, 10, 8]} />
          <meshBasicMaterial color="#f5cd5e" transparent opacity={0.2} depthWrite={false} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0.075, 0]}>
          <cylinderGeometry args={[0.035, 0.045, 0.05, 8]} />
          <primitive object={mats.brass} attach="material" />
        </mesh>
        <pointLight
          intensity={5.6}
          distance={11.5}
          decay={1.35}      /* below 2 the light actually CARRIES */
          color="#ffc978"
          castShadow={false}
        />
      </group>

      {/* ── THE AURA — OUTSIDE the body, so it survives real art ── */}
      {aura && (
        <group>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]} raycast={() => null}>
            <ringGeometry args={[0.48, 0.74, 32]} />
            <meshBasicMaterial color={aura} transparent opacity={0.34} depthWrite={false} toneMapped={false} />
          </mesh>
          <mesh position={[0, 0.95, 0]} scale={[1, 1.5, 1]} raycast={() => null}>
            <sphereGeometry args={[0.6, 16, 12]} />
            <meshBasicMaterial color={aura} transparent opacity={0.07} side={THREE.BackSide}
              depthWrite={false} toneMapped={false} />
          </mesh>
        </group>
      )}
    </group>
  );
}

// ── THE BOAT ─────────────────────────────────────────────────
// A skiff. The angler stands in it; the boat carries the same
// refs, so there is exactly one position in the whole renderer.
export function Boat3D({ posRef, yawRef, speedRef, children }) {
  const root = useRef();
  const wake = useRef();

  useFrame((state, dt) => {
    const g = root.current;
    if (!g || !posRef.current) return;
    const p = posRef.current;
    const d = Math.min(dt, 0.05);
    g.position.set(p.x, p.y, p.z);

    let diff = (yawRef.current || 0) - g.rotation.y;
    while (diff >  Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    g.rotation.y += diff * Math.min(1, d * 6);   // a boat turns like a boat

    const t = state.clock.elapsedTime;
    const sp = speedRef.current || 0;
    g.position.y = Math.sin(t * 1.15) * 0.045 + Math.sin(t * 0.61) * 0.03;
    g.rotation.z = Math.sin(t * 0.9) * 0.045 - sp * 0.03;
    g.rotation.x = Math.sin(t * 1.3) * 0.028 + sp * 0.02;
    if (wake.current) {
      wake.current.material.opacity = 0.05 + Math.min(0.3, sp * 0.22);
      wake.current.scale.setScalar(1 + Math.min(0.6, sp * 0.4));
    }
  });

  return (
    <group ref={root}>
      {/* hull */}
      <mesh position={[0, 0.16, 0]} castShadow>
        <cylinderGeometry args={[0.62, 0.44, 0.34, 10, 1, false]} />
        <meshStandardMaterial color="#4a3418" roughness={0.82} />
      </mesh>
      <mesh scale={1.045} position={[0, 0.16, 0]} raycast={() => null}>
        <cylinderGeometry args={[0.62, 0.44, 0.34, 10, 1, false]} />
        <meshBasicMaterial color={INK} side={THREE.BackSide} fog={false} />
      </mesh>
      {/* the prow */}
      <mesh position={[0, 0.2, -0.72]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <coneGeometry args={[0.42, 0.62, 8]} />
        <meshStandardMaterial color="#4a3418" roughness={0.82} />
      </mesh>
      {/* gunwale */}
      <mesh position={[0, 0.33, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.6, 0.038, 6, 20]} />
        <meshStandardMaterial color="#6b4a22" roughness={0.7} />
      </mesh>
      {/* deck boards + seat */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.3, 0]} receiveShadow>
        <circleGeometry args={[0.58, 12]} />
        <meshStandardMaterial color="#3a2a14" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.4, 0.28]}>
        <boxGeometry args={[0.9, 0.06, 0.2]} />
        <meshStandardMaterial color="#6b4a22" roughness={0.75} />
      </mesh>
      {/* bow lantern — the boat carries its own light out to THE DEEP */}
      <group position={[0, 0.62, -0.68]}>
        <mesh>
          <cylinderGeometry args={[0.03, 0.03, 0.34, 6]} />
          <meshStandardMaterial color="#5c421f" roughness={0.8} />
        </mesh>
        <mesh position={[0, 0.24, 0]}>
          <sphereGeometry args={[0.075, 10, 8]} />
          <meshBasicMaterial color="#ffe9a8" toneMapped={false} />
        </mesh>
        <pointLight position={[0, 0.24, 0]} intensity={4.2} distance={12} decay={1.4}
          color="#ffcf8a" castShadow={false} />
      </group>
      {/* the wake — one plane, opacity driven by speed */}
      <mesh ref={wake} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.015, 0.9]} raycast={() => null}>
        <planeGeometry args={[1.5, 2.6]} />
        <meshBasicMaterial color="#bfe8f2" transparent opacity={0.08} depthWrite={false} />
      </mesh>
      {children}
    </group>
  );
}
